<template>
  <div class="statistics-page min-h-screen">
    <!-- Контент -->
    <div class="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-6 py-6">
      <!-- Улучшенные табы -->
      <div class="mb-8">
        <div class="relative bg-white rounded-xl p-2 shadow-sm border border-gray-200">
          <nav class="flex space-x-1" aria-label="Tabs">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                activeTab === tab.id
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
                'relative px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ease-in-out flex-1 text-center cursor-pointer'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
        </div>
      </div>

      <!-- Загрузка -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>

      <!-- Ошибка -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <div class="text-sm font-medium text-red-800">Ошибка загрузки данных</div>
            <div class="mt-2 text-sm text-red-700">
              {{ error }}
            </div>
          </div>
        </div>
      </div>

      <!-- Контент табов с анимацией -->
      <div v-else class="tab-content-container">
        <transition name="tab-fade" mode="out-in">
          <!-- Таб статистики по сотрудникам (пользователям) -->
          <div v-if="activeTab === 'users'" key="users" class="!space-y-2.5">
            <div class="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <div class="flex items-center gap-2">
                <button @click="openCoursePicker" class="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white hover:bg-gray-50">
                  {{ selectedCourseLabel }}
                </button>
                <button v-if="selectedCourseId" @click="resetCourseSelection" class="text-xs text-gray-500 underline">Сбросить</button>
              </div>
              <button
                @click="downloadAttemptsReport"
                :disabled="isDownloadingAttempts"
                class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="!isDownloadingAttempts" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {{ isDownloadingAttempts ? 'Скачивание...' : 'Скачать отчет' }}
              </button>
            </div>
            <div v-for="userStat in usersStatistics" :key="userStat.userId" class="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <!-- Заголовок пользователя -->
              <div class="px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                   @click="toggleUserExpanded(userStat.userId)">
                <UserCard
                  :avatarUrl="getUserAvatar(userStat.userId)"
                  :title="getUserTitle(userStat.userId)"
                  :subtitle="getUserPosition(userStat.userId)"
                  shape="rounded-full"
                >
                  <template #right>
                    <svg :class="['w-5 h-5 text-gray-400 transition-transform duration-200', expandedUsers.includes(userStat.userId) ? 'rotate-180' : '']"
                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </template>
                </UserCard>
              </div>

              <!-- Детали пользователя с анимацией -->
              <transition name="expand">
                <div v-if="expandedUsers.includes(userStat.userId)" class="px-6 py-4 bg-gray-50">
                  <!-- Фильтруем курсы с totalAttempts = 0 -->
                  <div v-for="courseStats in userStat.statistics.filter(course => course.totalAttempts > 0)" :key="courseStats.id" class="mb-2.5 last:mb-0">
                    <!-- Карточка курса -->
                    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div class="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                           @click="toggleCourseExpanded(userStat.userId, courseStats.id)">
                        <div class="flex items-center justify-between">
                          <div class="flex-1">
                            <div class="text-lg font-medium text-gray-900 mb-2">{{ courseStats.title }}</div>
                            <div class="grid grid-cols-2 gap-4 text-sm">
                              <div class="flex items-center gap-2">
                                <div :class="[
                                  'w-3 h-3 rounded-full',
                                  courseStats.isCompleted ? 'bg-green-500' : 'bg-yellow-500'
                                ]"></div>
                                <span>{{ courseStats.isCompleted ? 'Завершен' : 'В процессе' }}</span>
                              </div>
                              <!-- Согласно требованиям показываем только средний балл -->
                              <div>
                                <span class="text-gray-500">Средний балл:</span>
                                <span class="ml-1 font-medium">{{ courseStats.averageScore ? courseStats.averageScore.toFixed(1) : '0' }}%</span>
                              </div>
                            </div>
                          </div>
                          <svg :class="['w-5 h-5 text-gray-400 transition-transform duration-200', isExpanded(userStat.userId, courseStats.id) ? 'rotate-180' : '']"
                               fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                          </svg>
                        </div>
                      </div>

                      <!-- Детали тестов -->
                      <transition name="expand">
                        <div v-if="isExpanded(userStat.userId, courseStats.id)" class="border-t border-gray-200 p-4 bg-gray-50">
                          <div class="font-medium text-gray-900 mb-3">Тесты курса</div>
                          <div class="space-y-3">
                            <div v-for="testStat in courseStats.testsStatistics" :key="testStat.id"
                                 class="bg-white rounded-lg p-3 border border-gray-200">
                              <div class="flex items-center justify-between mb-2">
                                <div class="text-sm font-medium text-gray-900">{{ testStat.title }}</div>
                                <span :class="[
                                  'px-2 py-1 text-xs rounded-full',
                                  testStat.attemptsCount === 0 ? 'bg-gray-100 text-gray-800' :
                                  testStat.isPassed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                ]">
                                  {{ testStat.attemptsCount === 0 ? 'Не начат' :
                                     testStat.isPassed ? 'Пройден' : 'Не пройден' }}
                                </span>
                              </div>
                              <div class="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span class="text-gray-500">Попыток:</span>
                                  <span class="ml-1 font-medium">{{ testStat.attemptsCount }}</span>
                                </div>
                                <!-- Согласно требованиям скрываем количество правильных, оставляем лучший балл -->
                                <div>
                                  <span class="text-gray-500">Лучший балл:</span>
                                  <span class="ml-1 font-medium">{{ testStat.bestScore }}%</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </transition>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Таб статистики по курсам -->
          <div v-else-if="activeTab === 'courses'" key="courses" class="space-y-6">
            <!-- Кнопка скачивания отчета по тестам -->
            <div class="mb-6">
              <button
                @click="downloadQuestionsReport"
                :disabled="isDownloadingQuestions"
                class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="!isDownloadingQuestions" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {{ isDownloadingQuestions ? 'Скачивание...' : 'Скачать отчет по тестам' }}
              </button>
            </div>
            <div v-for="course in coursesStatistics" :key="course.id" class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div class="px-6 py-5 border-b border-gray-200">
                <div class="flex items-center justify-between mb-4">
                  <div class="text-lg font-semibold text-gray-900">{{ course.title }}</div>
                  <button
                    v-if="course.testsStatistics.length > 0"
                    @click="toggleCourseTestsExpanded(course.id)"
                    class="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    <span class="text-sm">Тесты ({{ course.testsStatistics.length }})</span>
                    <svg :class="['w-5 h-5 transition-transform duration-200', expandedCourseTests.includes(course.id) ? 'rotate-180' : '']"
                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                </div>
                <div class="grid grid-cols-3 md:grid-cols-3 gap-6">
                  <div class="text-center bg-gray-50 p-3 rounded-xl">
                    <div class="text-2xl font-bold text-blue-600">{{ course.totalUsers }}</div>
                    <div class="text-sm text-gray-500">Всего пользователей</div>
                  </div>
                  <div class="text-center bg-gray-50 p-3 rounded-xl">
                    <div class="text-2xl font-bold text-green-600">{{ course.completedUsers }}</div>
                    <div class="text-sm text-gray-500">Завершили</div>
                  </div>
                  <!-- Используем averageScore если есть, иначе averageErrors -->
                  <div class="text-center bg-gray-50 p-3 rounded-xl">
                    <div class="text-2xl font-bold text-purple-600">
                      {{ course.averageScore ? course.averageScore.toFixed(1) + '%' :
                         (course.averageErrors ? course.averageErrors.toFixed(1) : '0') }}
                    </div>
                    <div class="text-sm text-gray-500">
                      {{ course.averageScore ? 'Средний балл' : 'Средние ошибки' }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Статистика по тестам -->
              <transition name="expand">
                <div v-if="course.testsStatistics.length > 0 && expandedCourseTests.includes(course.id)" class="p-6 bg-gray-50">
                  <div class="text-md font-medium text-gray-900 mb-4">Детали по тестам</div>

                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div v-for="test in course.testsStatistics" :key="test.id" class="bg-white rounded-2xl p-4 shadow-sm">
                      <div class="flex items-center justify-between gap-4 mb-3">
                        <div class="font-medium text-gray-900">{{ test.title }}</div>
                        <div class="flex gap-2">
                          <button v-if="test.questionsStatistics.length > 0" @click="showQuestionsModal(test)"
                                  class="text-blue-600 text-sm hover:text-blue-700 py-2 px-3 rounded-md bg-blue-50 hover:bg-blue-100 cursor-pointer transition-colors">
                            Статистика по вопросам
                          </button>
                          <button @click="showLeaderboardModal(test, course.id)"
                                  class="text-green-600 text-sm hover:text-green-700 py-2 px-3 rounded-md bg-green-50 hover:bg-green-100 cursor-pointer transition-colors">
                            Топ пользователей
                          </button>
                        </div>
                      </div>
                      <div class="flex items-center gap-5 text-sm">
                        <div class="flex justify-between gap-2">
                          <span class="text-gray-500">В среднем попыток:</span>
                          <span class="font-medium">{{ test.averageAttempts.toFixed(1) }}</span>
                        </div>
                        <div class="flex justify-between gap-2">
                          <span class="text-gray-500">Средний балл:</span>
                          <span class="font-medium">
                            {{ test.averageScore ? test.averageScore.toFixed(1) + '%' :
                               (test.averageErrors ? (100 - test.averageErrors * 10).toFixed(1) + '%' : '0%') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Таб последних результатов -->
          <div v-else-if="activeTab === 'events'" key="events" class="space-y-4">
            <div v-for="event in eventsStatistics"
                 :key="event.attemptId"
                 class="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">

              <div class="mb-3">
                <UserCard
                  :avatarUrl="getUserAvatar(event.userId)"
                  :title="getUserTitle(event.userId)"
                  :subtitle="`Тест: ${event.testTitle} · Курс: ${event.courseTitle}`"
                  size="md"
                  shape="rounded-full"
                >
                  <template #right>
                    <div class="text-right">
                      <p class="text-sm text-gray-500">{{ formatDate(event.completedAt) }}</p>
                      <span :class="[
                        'inline-block px-2 py-1 text-xs rounded-full mt-1',
                        event.isPassed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      ]">
                        {{ event.isPassed ? 'Пройден' : 'Не пройден' }}
                      </span>
                    </div>
                  </template>
                </UserCard>
              </div>

              <!-- === ИЗМЕНЕНИЯ ЗДЕСЬ === -->

              <!-- Новый блок со статистикой -->
              <div class="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm mb-4 border-t border-b border-gray-100 py-3">
                <div>
                  <span class="text-gray-500">Правильно:</span>
                  <span class="ml-1 font-medium text-green-600">
                  {{ event.correctAnswersCount }} / {{ event.totalQuestions }}
                  <span v-if="event.totalQuestions > 0" class="text-gray-500 font-normal">
                    ({{ Math.round((event.correctAnswersCount / event.totalQuestions) * 100) }}%)
            </span>
          </span>
                </div>
                <div>
                  <span class="text-gray-500">Ошибок:</span>
                  <span class="ml-1 font-medium text-red-600">{{ event.incorrectAnswersCount }}</span>
                </div>
                <div>
                  <span class="text-gray-500">Без ответа:</span>
                  <span class="ml-1 font-medium text-gray-600">{{ Math.max(0, event.unansweredQuestionsCount) }}</span>
                </div>
                <div>
                  <span class="text-gray-500">Время:</span>
                  <!-- Вам нужно будет добавить функцию formatTimeSpent в ваш <script> -->
                  <span class="ml-1 font-medium text-gray-600">{{ formatTimeSpent(event.timeSpent) }}</span>
                </div>
              </div>

              <!-- Новое поле: Причина неудачи (отображается только если тест не пройден) -->
              <div v-if="!event.isPassed && (event.failureReason !== null)" class="text-sm text-red-700 bg-red-50 rounded-lg p-3 mb-3 text-center">
                <strong>Причина неудачи:</strong> {{ formatFailureReason(event.failureReason) }}
              </div>

              <!-- === КОНЕЦ ИЗМЕНЕНИЙ === -->

              <button @click="showAnswers(event)"
                      class="w-full py-2 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-sm cursor-pointer">
                Показать детали ответов
              </button>
            </div>
            
            <!-- Информация о пагинации и кнопка "Загрузить еще" -->
            <div v-if="eventsStatistics.length > 0" class="mt-6 text-center">
              <p class="text-sm text-gray-500 mb-3">
                Показано {{ eventsStatistics.length }} из {{ eventsPagination.total }} событий
              </p>
              
              <button 
                v-if="eventsPagination.hasNext"
                @click="loadMoreEvents"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="eventsLoading"
              >
                <span v-if="!eventsLoading">Загрузить еще</span>
                <span v-else class="flex items-center justify-center">
                  <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Загрузка...
                </span>
              </button>
            </div>
            
            <!-- Сообщение, если нет событий -->
            <div v-if="eventsStatistics.length === 0 && !eventsLoading" class="text-center py-8">
              <p class="text-gray-500">Нет доступных событий</p>
            </div>
            
            <!-- Индикатор загрузки при первой загрузке -->
            <div v-if="eventsLoading && eventsStatistics.length === 0" class="text-center py-8">
              <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="mt-2 text-gray-500">Загрузка событий...</p>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Модальное окно статистики по вопросам -->
    <transition name="modal-fade" appear>
      <div v-if="selectedTest" class="fixed inset-0 bg-white/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <transition name="modal-scale" appear>
          <div class="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl border border-gray-200">
            <div class="px-6 py-4 border-b border-gray-200 flex items-center !justify-between">
              <div class="text-lg font-semibold text-gray-900">Статистика по вопросам</div>
              <button @click="selectedTest = null" class="text-gray-400 hover:text-gray-600">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div class="mb-4">
                <div class="font-medium text-gray-900">{{ selectedTest.title }}</div>
              </div>

              <div v-if="selectedTest.questionsStatistics.length > 0" class="space-y-4 mb-10">
                <div v-for="question in selectedTest.questionsStatistics" :key="question.id"
                     class="bg-gray-50 rounded-2xl p-4">
                  <p class="font-medium text-gray-900 mb-3" v-html="question.question"></p>
                  <div class="space-y-2">
                    <div v-for="option in question.optionsStatistics" :key="option.option"
                         class="flex justify-between items-center p-2 rounded-lg bg-white border border-gray-200">
                      <div class="text-gray-600 flex-1 mr-3 flex items-center gap-2">
                        <div v-html="option.option"></div>
                      </div>
                      <div class="flex items-center gap-2">
                        <div class="w-20 bg-gray-200 rounded-full h-2">
                          <div class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                               :style="{ width: `${option.percentage}%` }"></div>
                        </div>
                        <span class="font-medium w-12 text-right">{{ option.percentage }}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <transition name="modal-fade" appear>
      <div v-if="showCoursePicker" class="fixed inset-0 bg-white/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <transition name="modal-scale" appear>
          <div class="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden shadow-xl border border-gray-200">
            <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div class="text-lg font-semibold text-gray-900">Выбор курса</div>
              <button @click="closeCoursePicker" class="text-gray-400 hover:text-gray-600">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div class="p-6">
              <input v-model="courseSearch" type="text" placeholder="Найдите курс" class="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-[#574AEA] focus:border-transparent" />
              <div class="max-h-80 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-xl">
                <div v-for="c in filteredCourses" :key="c.id" class="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer" @click="selectCourse(c)">
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-gray-900 text-sm truncate">{{ c.title }}</div>
                  </div>
                  <div class="text-[#574AEA] text-sm font-medium">Выбрать</div>
                </div>
                <div v-if="!filteredCourses.length" class="p-3 text-sm text-gray-500">Ничего не найдено</div>
              </div>
              <div class="mt-4 flex justify-end">
                <button @click="resetCourseSelection(); closeCoursePicker()" class="py-2 px-4 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors">Сбросить</button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- Модальное окно топа пользователей -->
    <transition name="modal-fade" appear>
      <div v-if="selectedLeaderboard" class="fixed inset-0 bg-white/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <transition name="modal-scale" appear>
          <div class="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-xl border border-gray-200">
            <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div class="text-lg font-semibold text-gray-900">Топ пользователей</div>
              <button @click="selectedLeaderboard = null" class="text-gray-400 hover:text-gray-600">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div class="mb-4">
                <div class="font-medium text-gray-900 mb-2">{{ selectedLeaderboard?.testTitle }}</div>

                <!-- Переключатель сортировки -->
                <div class="flex items-center gap-4 mb-4">
                  <span class="text-sm text-gray-600">Сортировка:</span>
                  <div class="flex bg-gray-100 rounded-lg p-1">
                    <button
                      @click="leaderboardSortBy = 'score'; loadLeaderboard()"
                      :class="[
                        'px-3 py-1 text-sm rounded-md transition-colors',
                        leaderboardSortBy === 'score'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      ]">
                      По результату
                    </button>
                    <button
                      @click="leaderboardSortBy = 'time'; loadLeaderboard()"
                      :class="[
                        'px-3 py-1 text-sm rounded-md transition-colors',
                        leaderboardSortBy === 'time'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      ]">
                      По времени
                    </button>
                  </div>
                </div>
              </div>

              <!-- Загрузка -->
              <div v-if="leaderboardLoading" class="flex justify-center items-center py-8">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>

              <!-- Ошибка -->
              <div v-else-if="leaderboardError" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div class="text-sm text-red-700">{{ leaderboardError }}</div>
              </div>

              <!-- Список пользователей -->
              <div v-else-if="leaderboardData?.topUsers?.length > 0" class="space-y-3">
                <div v-for="(user, index) in leaderboardData.topUsers" :key="user.userId"
                     class="px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors rounded-lg border">
                  <div class="flex items-center gap-4">
                    <!-- Позиция -->
                    <div class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-white"
                         :class="[
                           index === 0 ? 'bg-yellow-500' :
                           index === 1 ? 'bg-gray-400' :
                           index === 2 ? 'bg-orange-600' : 'bg-gray-300'
                         ]">
                      {{ index + 1 }}
                    </div>

                    <UserCard
                      :avatarUrl="getUserAvatar(user.userId)"
                      :title="getUserTitle(user.userId)"
                      :subtitle="getUserPosition(user.userId)"
                      shape="rounded-full"
                    >
                      <template #right>
                        <div class="text-right">
                          <div class="font-bold text-lg" :class="[
                            leaderboardSortBy === 'score' ? 'text-green-600' : 'text-blue-600'
                          ]">
                            {{ leaderboardSortBy === 'score' ? user.bestScore.toFixed(1) + '%' : formatTimeSpent(user.timeSpent) }}
                          </div>
                          <div class="text-sm text-gray-500">
                            {{ leaderboardSortBy === 'score' ? 'Результат' : 'Время' }}
                          </div>
                          <div class="text-xs text-gray-400 mt-1">
                            {{ leaderboardSortBy === 'score' ? formatTimeSpent(user.timeSpent) : user.bestScore.toFixed(1) + '%' }} •
                            {{ user.correctAnswers }} правильных •
                            {{ user.totalAttempts }} попыт{{ user.totalAttempts === 1 ? 'ка' : user.totalAttempts < 5 ? 'ки' : 'ок' }}
                          </div>
                        </div>
                      </template>
                    </UserCard>
                  </div>
                </div>
              </div>

              <!-- Пустой результат -->
              <div v-else class="text-center py-8 text-gray-500">
                <svg class="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                </svg>
                <div class="text-lg font-medium mb-2">Нет данных</div>
                <div class="text-sm">Пока нет успешных прохождений этого теста</div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- Модальное окно ответов -->
    <transition name="modal-fade" appear>
      <div v-if="selectedEvent" class="fixed inset-0 bg-white/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <transition name="modal-scale" appear>
          <div class="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl border border-gray-200">
            <div class="px-6 py-4 border-b border-gray-200 flex items-center !justify-between">
              <div class="text-lg font-semibold text-gray-900">Детали ответов</div>
              <button @click="selectedEvent = null" class="text-gray-400 hover:text-gray-600 cursor-pointer">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div class="mb-6">
                <div class="font-medium text-gray-900 mb-2">{{ selectedEvent.testTitle }}</div>
                <UserCard
                  :avatarUrl="getUserAvatar(selectedEvent.userId)"
                  :title="getUserTitle(selectedEvent.userId)"
                  :subtitle="selectedEvent.courseTitle"
                  size="md"
                  shape="rounded-full"
                />
              </div>

              <div v-if="selectedEvent.questionResults.length > 0" class="space-y-6 mb-10">
                <div v-for="(result, index) in selectedEvent.questionResults" :key="result.questionId" class="border border-gray-200 rounded-lg p-6">
                  <div class="flex items-start justify-between mb-4">
                    <div class="flex-1">
                      <div class="inline-block w-6 h-6 bg-primary text-white text-xs rounded-full text-center leading-6 mr-3 mb-2">{{ index + 1 }}</div>
                      <div class="font-medium text-gray-900 inline" v-html="result.question"></div>
                    </div>
                    <div class="flex items-center gap-2 ml-4">
                      <span :class="[
                        'px-2 py-1 text-xs rounded',
                        result.isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      ]">
                        {{ result.isCorrect ? 'Верно' : result.userAnswers.length > 0 ? 'Неверно' : 'Не отвечен' }}
                      </span>
                      <span v-if="result.isCritical" class="px-2 py-1 text-xs rounded bg-orange-100 text-orange-800">
                        Критический
                      </span>
                    </div>
                  </div>

                  <div class="text-sm space-y-3">
                    <div>
                      <span class="font-medium text-gray-700">Ответ пользователя:</span>
                      <span class="ml-2 text-gray-900">{{ result.userAnswers.length > 0 ? result.userAnswers.join(', ') : 'Не отвечен' }}</span>
                    </div>
                    <div>
                      <span class="font-medium text-gray-700">Правильные ответы:</span>
                      <span class="ml-2 text-green-600">{{ result.correctAnswers.join(', ') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { usePortalStore } from '@/stores/portalStore'
import apiService from '@/services/api'
import { useToast } from "vue-toastification"
import UserCard from '@/components/ui/UserCard.vue'

const toast = useToast()
const portalStore = usePortalStore()

// Состояние
const activeTab = ref('courses')
const loading = ref(false)
const error = ref(null)
const selectedEvent = ref(null)
const selectedTest = ref(null)
const selectedLeaderboard = ref(null)
const leaderboardData = ref(null)
const leaderboardLoading = ref(false)
const leaderboardError = ref(null)
const leaderboardSortBy = ref('score')
const expandedUsers = ref([])
const expandedCourses = ref([])
const expandedCourseTests = ref([])

// Состояние скачивания отчетов
const isDownloadingAttempts = ref(false)
const isDownloadingQuestions = ref(false)

// Данные
const coursesStatistics = ref([])
const usersStatistics = ref([])
const eventsStatistics = ref([])

// Пагинация для событий
const eventsPagination = ref({
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0,
  hasNext: false,
  hasPrev: false
})
const eventsLoading = ref(false)

// Выбор курса для отчета
const selectedCourseId = ref('')
const showCoursePicker = ref(false)
const courseSearch = ref('')
const coursesSimple = computed(() => (coursesStatistics.value || []).map(c => ({ id: c.id, title: c.title })))
const filteredCourses = computed(() => {
  const q = (courseSearch.value || '').trim().toLowerCase()
  const list = coursesSimple.value || []
  if (!q) return list
  return list.filter(c => (c.title || '').toLowerCase().includes(q))
})
const selectedCourseLabel = computed(() => {
  if (!selectedCourseId.value) return 'Все курсы'
  const c = (coursesSimple.value || []).find(x => x.id === selectedCourseId.value)
  return c?.title || 'Все курсы'
})

const openCoursePicker = () => { courseSearch.value = ''; showCoursePicker.value = true }
const closeCoursePicker = () => { showCoursePicker.value = false }
const selectCourse = (c) => { selectedCourseId.value = c?.id || ''; showCoursePicker.value = false }
const resetCourseSelection = () => { selectedCourseId.value = '' }

// Табы
const tabs = [
  { id: 'users', name: 'По сотрудникам' },
  { id: 'courses', name: 'По курсам' },
  { id: 'events', name: 'Последние результаты' }
]

// Методы
const getUserName = (userId) => {
  if (!userId) return 'Неизвестный пользователь'

  const user = portalStore.portalData.allUsers.find((i) => i.ID === userId)
  if (!user) return `Пользователь ${userId}`

  return `${user?.NAME || ''} ${user?.LAST_NAME || ''}`.trim() || `${user?.EMAIL || ''}` || `Пользователь ${userId}`
}

const getUserTitle = (userId) => {
  const user = portalStore.portalData.allUsers.find((i) => i.ID === userId)
  if (!user) return getUserName(userId)
  return `${user?.LAST_NAME || ''} ${user?.NAME || ''}`.trim() || getUserName(userId)
}

const getUserPosition = (userId) => {
  const user = portalStore.portalData.allUsers.find((i) => i.ID === userId)
  return user?.WORK_POSITION || ''
}

const getUserAvatar = (userId) => {
  const user = portalStore.portalData.allUsers.find((i) => i.ID === userId)
  return user?.PERSONAL_PHOTO || 'https://cdn-icons-png.flaticon.com/128/3177/3177440.png'
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const toggleUserExpanded = (userId) => {
  const index = expandedUsers.value.indexOf(userId)
  if (index > -1) {
    expandedUsers.value.splice(index, 1)
  } else {
    expandedUsers.value.push(userId)
  }
}

const toggleCourseExpanded = (userId, courseId) => {
  const key = `${userId}-${courseId}`
  const index = expandedCourses.value.indexOf(key)
  if (index > -1) {
    expandedCourses.value.splice(index, 1)
  } else {
    expandedCourses.value.push(key)
  }
}

const toggleCourseTestsExpanded = (courseId) => {
  const index = expandedCourseTests.value.indexOf(courseId)
  if (index > -1) {
    expandedCourseTests.value.splice(index, 1)
  } else {
    expandedCourseTests.value.push(courseId)
  }
}

const isExpanded = (userId, courseId) => {
  return expandedCourses.value.includes(`${userId}-${courseId}`)
}

const showAnswers = (event) => {
  selectedEvent.value = event
}

const showQuestionsModal = (test) => {
  selectedTest.value = test
}

const showLeaderboardModal = (test, courseId) => {
  selectedLeaderboard.value = {
    testId: test.id,
    testTitle: test.title,
    courseId: courseId
  }
  leaderboardSortBy.value = 'score'
  loadLeaderboard()
}

const loadLeaderboard = async () => {
  if (!selectedLeaderboard.value) return

  leaderboardLoading.value = true
  leaderboardError.value = null

  try {
    const response = await apiService.getTestLeaderboard(
      selectedLeaderboard.value.courseId,
      selectedLeaderboard.value.testId,
      leaderboardSortBy.value
    )
    leaderboardData.value = response
  } catch (err) {
    console.error('Ошибка загрузки топа пользователей:', err)
    leaderboardError.value = 'Не удалось загрузить топ пользователей'
    toast.error('Не удалось загрузить топ пользователей')
  } finally {
    leaderboardLoading.value = false
  }
}

const loadCoursesStatistics = async () => {
  try {
    coursesStatistics.value = await apiService.getCoursesStatistics()
  } catch (err) {
    console.error('Ошибка загрузки статистики курсов:', err)
    throw new Error('Не удалось загрузить статистику по курсам')
  }
}

const loadUsersStatistics = async () => {
  try {
    usersStatistics.value = await apiService.getUsersStatistics()
  } catch (err) {
    console.error('Ошибка загрузки статистики пользователей:', err)
    throw new Error('Не удалось загрузить статистику по пользователям')
  }
}

// Загрузка первой страницы событий
const loadEventsStatistics = async () => {
  try {
    eventsLoading.value = true
    eventsPagination.value.page = 1
    
    const response = await apiService.getEventsStatistics(1, eventsPagination.value.limit)
    
    // Обновляем данные пагинации
    eventsPagination.value = {
      page: response.page,
      limit: response.limit,
      total: response.total,
      totalPages: response.totalPages,
      hasNext: response.hasNext,
      hasPrev: response.hasPrev
    }
    
    // Обновляем список событий
    eventsStatistics.value = response.data || []
  } catch (err) {
    console.error('Ошибка загрузки событий:', err)
    throw new Error('Не удалось загрузить события прохождений')
  } finally {
    eventsLoading.value = false
  }
}

// Загрузка следующей страницы событий
const loadMoreEvents = async () => {
  if (!eventsPagination.value.hasNext || eventsLoading.value) return
  
  try {
    eventsLoading.value = true
    const nextPage = +eventsPagination.value.page + 1
    
    const response = await apiService.getEventsStatistics(nextPage, eventsPagination.value.limit)
    
    // Обновляем данные пагинации
    eventsPagination.value = {
      page: response.page,
      limit: response.limit,
      total: response.total,
      totalPages: response.totalPages,
      hasNext: response.hasNext,
      hasPrev: response.hasPrev
    }
    
    // Добавляем новые события к существующим
    eventsStatistics.value = [...eventsStatistics.value, ...(response.data || [])]
  } catch (err) {
    console.error('Ошибка загрузки дополнительных событий:', err)
    toast.error('Не удалось загрузить дополнительные события')
  } finally {
    eventsLoading.value = false
  }
}

const loadAllStatistics = async () => {
  loading.value = true
  error.value = null

  try {
    await Promise.all([
      loadCoursesStatistics(),
      loadUsersStatistics(),
      loadEventsStatistics()
    ])
  } catch (err) {
    error.value = err.message
    toast.error(err.message)
  } finally {
    loading.value = false
  }
}

const formatTimeSpent = (seconds) => {
  if (seconds === null || seconds === undefined) return 'N/A';
  if (seconds < 60) return `${seconds}с`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes}м ${remainingSeconds}с`;
};

const formatFailureReason = (reason) => {
  const reasonMap = [
    "Время вышло",
    "Неверный ответ на критический вопрос",
    "Недостаточно правильных ответов",
    "Попытка прервана пользователем"
  ]

  return reasonMap[reason] || reason; // Возвращает текст или исходный ключ, если совпадения нет
};

// Скачивание отчета по попыткам
const downloadAttemptsReport = async () => {
  if (isDownloadingAttempts.value) return

  try {
    isDownloadingAttempts.value = true
    const response = selectedCourseId.value
      ? await apiService.downloadAttemptsReportByCourse(selectedCourseId.value)
      : await apiService.downloadAttemptsReport()

    // Создаем URL для скачивания
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url

    // Определяем имя файла
    const contentDisposition = response.headers['content-disposition']
    let filename = 'Отчет_по_попыткам.xlsx'
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)
      if (filenameMatch) {
        filename = filenameMatch[1]
      }
    }

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    toast.success('Отчет успешно скачан')
  } catch (error) {
    console.error('Ошибка при скачивании отчета по попыткам:', error)
    toast.error('Ошибка при скачивании отчета')
  } finally {
    isDownloadingAttempts.value = false
  }
}

// Скачивание отчета по вопросам
const downloadQuestionsReport = async () => {
  if (isDownloadingQuestions.value) return

  try {
    isDownloadingQuestions.value = true
    const response = selectedCourseId.value
      ? await apiService.downloadQuestionsReportByCourse(selectedCourseId.value)
      : await apiService.downloadQuestionsReport()

    // Создаем URL для скачивания
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url

    // Определяем имя файла
    const contentDisposition = response.headers['content-disposition']
    let filename = 'Отчет_по_тестам.xlsx'
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)
      if (filenameMatch) {
        filename = filenameMatch[1]
      }
    }

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    toast.success('Отчет успешно скачан')
  } catch (error) {
    console.error('Ошибка при скачивании отчета по вопросам:', error)
    toast.error('Ошибка при скачивании отчета')
  } finally {
    isDownloadingQuestions.value = false
  }
}

// Инициализация
onMounted(() => {
  loadAllStatistics()
})
</script>

<style scoped>
/* Анимации для табов */
.tab-fade-enter-active, .tab-fade-leave-active {
  transition: all 0.3s ease;
}

.tab-fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.tab-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Анимации для раскрытия */
.expand-enter-active, .expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from, .expand-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.expand-enter-to, .expand-leave-from {
  opacity: 1;
  max-height: 1000px;
}

/* Стили для контейнера табов */
.tab-content-container {
  position: relative;
}

/* Анимации для модальных окон */
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.modal-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.modal-scale-enter-active, .modal-scale-leave-active {
  transition: all 0.3s ease;
  transform: scale(1);
}

.modal-scale-enter-from, .modal-scale-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

/* Мобильная адаптивность */
@media (max-width: 768px) {
  .statistics-page {
    padding: 0;
    margin: 0;
  }

  /* Основной контейнер */
  .max-w-\[900px\].mx-auto.px-4.sm\:px-6.lg\:px-6.py-6 {
    max-width: 100%;
    padding: 12px;
    margin: 0;
  }

  /* Табы адаптация */
  .mb-8 {
    margin-bottom: 20px;
  }

  .bg-white.rounded-xl.p-2.shadow-sm.border.border-gray-200 {
    padding: 8px;
    border-radius: 12px;
    margin: 0 4px;
  }

  .flex.space-x-1 {
    gap: 4px;
  }

  .px-6.py-3.rounded-lg.font-medium.text-sm {
    padding: 10px 12px;
    font-size: 0.8rem;
    border-radius: 8px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Карточки пользователей */
  .space-y-2\.5 {
    gap: 16px;
  }

  .space-y-2\.5 > * + * {
    margin-top: 16px;
  }

  .bg-white.rounded-xl.border.border-gray-200 {
    border-radius: 16px;
    margin: 0 4px;
  }

  .px-4.py-3 {
    padding: 16px;
  }

  .flex.items-center.gap-4 {
    gap: 12px;
  }

  .w-12.h-12 {
    width: 40px;
    height: 40px;
  }

  .w-10.h-10 {
    width: 32px;
    height: 32px;
  }

  .w-8.h-8 {
    width: 28px;
    height: 28px;
  }

  .text-lg {
    font-size: 1rem;
  }

  .text-sm {
    font-size: 0.875rem;
  }

  /* Детали пользователя */
  .mb-2\.5 {
    margin-bottom: 16px;
  }

  .p-4 {
    padding: 16px;
  }

  /* Статистика курса */
  .grid.grid-cols-2.md\:grid-cols-4.gap-4 {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin-bottom: 12px;
  }

  .grid.grid-cols-2.md\:grid-cols-3.gap-3 {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  /* Статистика по курсам */
  .space-y-6 {
    gap: 20px;
  }

  .space-y-6 > * + * {
    margin-top: 20px;
  }

  .px-6.py-5 {
    padding: 16px;
  }

  .grid.grid-cols-5.md\:grid-cols-5.gap-6 {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .text-center.bg-gray-50.p-3.rounded-xl {
    padding: 12px 8px;
    border-radius: 12px;
  }

  .text-2xl {
    font-size: 1.25rem;
  }

  /* Детали тестов */
  .grid.grid-cols-1.md\:grid-cols-2.lg\:grid-cols-3.gap-4 {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .bg-white.rounded-2xl.p-4 {
    padding: 16px;
    border-radius: 16px;
  }

  /* События */
  .space-y-4 {
    gap: 16px;
  }

  .space-y-4 > * + * {
    margin-top: 16px;
  }

  .flex.items-start.justify-between {
    flex-direction: column;
    gap: 12px;
  }

  .flex.items-.gap-3 {
    gap: 8px;
    align-items: center;
  }

  .text-right {
    text-align: left;
    align-self: stretch;
  }

  /* Модальные окна */
  .max-w-\[350px\] {
    max-width: calc(100vw - 32px);
    width: 100%;
    margin: 16px;
    height: auto;
    max-height: calc(100vh - 32px);
  }

  .max-w-\[450px\] {
    max-width: calc(100vw - 32px);
    width: 100%;
    margin: 16px;
    height: auto;
    max-height: calc(100vh - 32px);
  }

  .p-6 {
    padding: 16px;
  }

  .max-h-\[calc\(90vh-80px\)\] {
    max-height: calc(100vh - 120px);
  }

  /* Статистика вопросов */
  .bg-gray-50.rounded-2xl.p-4 {
    padding: 12px;
    border-radius: 12px;
  }

  .space-y-4 > * + * {
    margin-top: 12px;
  }

  .flex.justify-between.items-center {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .flex.items-center.gap-2 {
    gap: 8px;
    justify-content: space-between;
  }

  .w-20 {
    width: 60px;
  }

  .w-12 {
    width: 40px;
  }

  /* Детали ответов */
  .border.border-gray-200.rounded-lg.p-4 {
    padding: 12px;
    border-radius: 12px;
  }

  .ml-8 {
    margin-left: 24px;
  }

  .space-y-2 {
    gap: 8px;
  }

  .space-y-2 > * + * {
    margin-top: 8px;
  }
}

@media (max-width: 480px) {
  /* Дополнительная компактность для маленьких экранов */
  .max-w-\[900px\].mx-auto.px-4.sm\:px-6.lg\:px-6.py-6 {
    padding: 8px;
  }

  .bg-white.rounded-xl.p-2.shadow-sm.border.border-gray-200 {
    padding: 4px;
    margin: 0 2px;
  }

  .px-6.py-3.rounded-lg.font-medium.text-sm {
    padding: 8px 10px;
    font-size: 0.75rem;
    min-height: 40px;
  }
  .grid.grid-cols-5.md\:grid-cols-5.gap-6 {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .text-center.bg-gray-50.p-3.rounded-xl {
    padding: 8px;
  }

  .text-2xl {
    font-size: 1.125rem;
  }

  .text-lg {
    font-size: 0.9rem;
  }

  .text-sm {
    font-size: 0.8rem;
  }

  .grid.grid-cols-2.md\:grid-cols-4.gap-4 {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .w-12.h-12 {
    width: 32px;
    height: 32px;
  }

  .w-10.h-10 {
    width: 28px;
    height: 28px;
  }

  .w-8.h-8 {
    width: 24px;
    height: 24px;
  }

  .max-w-\[350px\] {
    max-width: calc(100vw - 16px);
    margin: 8px;
  }

  .max-w-\[450px\] {
    max-width: calc(100vw - 16px);
    margin: 8px;
  }

  .ml-8 {
    margin-left: 16px;
  }

  .bg-gray-50.rounded-2xl.p-4 {
    padding: 8px;
  }

  .w-20 {
    width: 50px;
  }

  .w-12 {
    width: 32px;
  }
}
</style>
